import api from "@/utils/api";

/**
 * user-events 내부 수집 API로 이벤트 전송 (범용).
 * fire-and-forget — 실패해도 서비스에 영향 없음.
 *
 * @param {string} eventName
 * @param {object} properties
 * @param {string} eventCategory - 기본 "funnel". 공유 이벤트는 "conversion".
 */
export function trackUserEvent(eventName, properties = {}, eventCategory = "funnel") {
  try {
    api
      .post("/api/v1/user-events", buildUserEventPayload(eventName, properties, eventCategory))
      .catch(() => {});
  } catch {
    // 절대 throw 하지 않음
  }
}

/**
 * 공유하기 관련 이벤트 — trackUserEvent의 event_category="conversion" 특화.
 */
export function trackShareEvent(eventName, properties = {}) {
  trackUserEvent(eventName, properties, "conversion");
}

/**
 * 전량 행동데이터 미러용 단일 이벤트 페이로드 (P0-2/NZSC-3208). behaviorQueue 가 모아서 배치 전송.
 * event_id = 멱등키(재시도 중복 dedup). _track_only 는 배치 최상위에 붙고 컨트롤러가 bridge 스킵 판단.
 */
export function buildMirrorEvent(eventName, properties = {}) {
  return { ...buildUserEventPayload(eventName, properties, "behavior"), event_id: newEventId() };
}

/**
 * P0-2 점진 롤아웃 코호트 — anonymous_id 해시로 결정적 분류.
 * 한 유저는 전부 미러 or 전부 안 함 → 퍼널 단계 연속성 유지(이벤트 단위 샘플은 퍼널 깨짐).
 * ratePct(0~100) 만큼의 유저만 미러. CH 컨슈머 부하 보며 단계적으로 100 까지 상향.
 */
export function isInMirrorCohort(ratePct) {
  if (ratePct >= 100) return true;
  if (ratePct <= 0) return false;
  try {
    const id = getOrCreateAnonymousId();
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h % 100 < ratePct;
  } catch {
    return false;
  }
}

function newEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * 공유 뷰어 진입 시 localStorage에 referral 정보 저장
 */
export function setShareReferral(reportSeq) {
  try {
    localStorage.setItem(
      "share_referral",
      JSON.stringify({
        reportSeq,
        anonymous_id: getOrCreateAnonymousId(),
        timestamp: Date.now(),
      }),
    );
  } catch {
    // localStorage 접근 실패 시 무시
  }
}

/**
 * 로그인/회원가입 완료 시 share_referral 확인 후 share_conversion 이벤트 발송
 * 7일 이내 referral만 유효
 */
export function checkAndTrackShareConversion() {
  try {
    const referral = localStorage.getItem("share_referral");
    if (!referral) return;

    const data = JSON.parse(referral);
    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

    if (Date.now() - data.timestamp < SEVEN_DAYS_MS) {
      trackShareEvent("share_conversion", {
        anonymous_id: data.anonymous_id,
        reportSeq: data.reportSeq,
      });
    }

    localStorage.removeItem("share_referral");
  } catch {
    // 파싱 실패 등 무시
  }
}


// 다방 인입 세션(NZSC-3205) — 자체수집 이벤트에도 channel=dabang 첨부 (CH 에서 파트너 세그먼트 직필터).
function channelProps() {
  try {
    return window.sessionStorage.getItem("dabang_entered") === "1" ? { channel: "dabang" } : {};
  } catch {
    return {};
  }
}

function buildUserEventPayload(eventName, properties, eventCategory) {
  const utm = getSessionUtm();
  return {
    event_name: eventName,
    event_category: eventCategory,
    // 이벤트 "생성" 시각(ISO UTC) — NZSC-3707. consumer가 event_time으로 사용(부재 시 수신 시각).
    // 재전송(재시도 큐·SQS 재배달)에도 이 값이 보존돼 같은 event_id가 byte-동일 행이 되고
    // ReplacingMergeTree 멱등이 실제로 동작한다(없으면 재전송마다 수신 시각으로 갈라져 영구 중복).
    timestamp: new Date().toISOString(),
    anonymous_id: getOrCreateAnonymousId(),
    session_id: getOrTouchSessionId(),
    utm_source: utm.utm_source || "",
    utm_medium: utm.utm_medium || "",
    utm_campaign: utm.utm_campaign || "",
    page_url: window.location.href,
    referrer: document.referrer,
    properties: JSON.stringify({ ...channelProps(), ...properties }),
    device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
    os: navigator.platform,
    browser: navigator.userAgent.split(" ").pop(),
  };
}

/**
 * 세션 ID — 30분 무활동이면 새 세션 (직귀율·세션당 깊이 분석용). 이벤트마다 touch.
 */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
function getOrTouchSessionId() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem("nz_sid");
    if (raw) {
      const s = JSON.parse(raw);
      if (now - s.t < SESSION_TIMEOUT_MS) {
        s.t = now;
        sessionStorage.setItem("nz_sid", JSON.stringify(s));
        return s.id;
      }
    }
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(now);
    sessionStorage.setItem("nz_sid", JSON.stringify({ id, t: now }));
    return id;
  } catch {
    return "";
  }
}

/**
 * 유입 UTM — 랜딩 순간 URL의 utm_* 을 세션 동안 유지 (이후 페이지 이동해도 첫 유입 채널 귀속).
 */
function getSessionUtm() {
  try {
    const saved = sessionStorage.getItem("nz_utm");
    if (saved) return JSON.parse(saved);
    const q = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
    };
    if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
      sessionStorage.setItem("nz_utm", JSON.stringify(utm));
    }
    return utm;
  } catch {
    return {};
  }
}

/**
 * anonymous_id를 localStorage에서 가져오거나 새로 생성
 */
function getOrCreateAnonymousId() {
  try {
    let id = localStorage.getItem("nz_anonymous_id");
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
            });
      localStorage.setItem("nz_anonymous_id", id);
    }
    return id;
  } catch {
    return "unknown";
  }
}
